// V9.23 · Novo lançamento manual: Receita · Despesa · Dedução · Transferência · Ajuste
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import FornecedorPicker from './FornecedorPicker';

const KINDS = [
  ['receita', 'Receita', 'RECEITA'],
  ['despesa', 'Despesa', 'DESPESA'],
  ['deducao', 'Dedução', 'DEDUCAO'],
  ['transferencia', 'Transferência', null],
  ['ajuste', 'Ajuste de saldo', null],
];

const compFromDate = (d) => {
  const s = String(d || '').slice(0, 10);
  return s.length >= 7 ? `${s.slice(5, 7)}/${s.slice(0, 4)}` : '';
};

export const LancamentoModal = ({ open, onOpenChange, onSaved, entry,
  planoContas = [], unidades = [], segmentos = [], centrosCusto = [], accounts = [] }) => {
  const [kind, setKind] = useState('despesa');
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      const k = String(entry.tipo || '').toLowerCase() === 'ajuste' ? 'ajuste'
        : String(entry.tipo || '').toLowerCase();
      setKind(KINDS.some(([kk]) => kk === k) ? k : 'despesa');
      setForm({
        data: (entry.data || '').slice(0, 10), valor: String(Math.abs(entry.valor || 0)),
        historico: entry.historico || '', cod_conta: entry.cod_conta || '',
        cod_unidade: entry.cod_unidade || '', cod_seg: entry.cod_seg || 'GERAL',
        cod_cc: entry.cod_cc || '', account_id: entry.account_id || '',
        due_date: (entry.due_date || '').slice(0, 10),
        fornecedor_id: entry.fornecedor_id || null, fornecedor_nome: entry.fornecedor_cliente || null,
        doc: entry.doc || '', forma_pgto: entry.forma_pgto || '', observacoes: entry.observacoes || '',
      });
    } else {
      setKind('despesa');
      setForm({
        data: new Date().toISOString().slice(0, 10), valor: '', historico: '', cod_conta: '',
        cod_unidade: unidades[0]?.cod_unidade || '', cod_seg: 'GERAL', cod_cc: '',
        account_id: '', due_date: '', fornecedor_id: null, fornecedor_nome: null,
        doc: '', forma_pgto: '', observacoes: '',
      });
    }
  }, [open, entry, unidades]);

  const tipoAlvo = useMemo(() => (KINDS.find(([k]) => k === kind) || [])[2], [kind]);
  const contasDoTipo = useMemo(
    () => planoContas.filter((c) => c.tipo === tipoAlvo && c.ativo !== false),
    [planoContas, tipoAlvo]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const salvar = async () => {
    if (!form.historico?.trim()) return toast.error('Informe o histórico.');
    if (!form.valor || Number(form.valor) === 0) return toast.error('Informe o valor.');
    if (tipoAlvo && !form.cod_conta) return toast.error('Selecione a categoria.');
    if (kind === 'transferencia' && (!form.account_id || !form.account_to_id))
      return toast.error('Selecione a conta de origem e destino.');
    if (kind === 'ajuste' && !form.account_id) return toast.error('Selecione a conta do ajuste.');
    if (['receita', 'despesa'].includes(kind) && !form.account_id && !form.due_date)
      return toast.error('Selecione a conta de recebimento/pagamento — ou informe o vencimento se ainda não foi pago.');
    setBusy(true);
    try {
      const body = {
        kind, data: form.data, competencia: compFromDate(form.data),
        due_date: form.due_date || null,
        valor: Number(String(form.valor).replace(',', '.')),
        historico: form.historico.trim(),
        cod_conta: form.cod_conta || null, cod_unidade: form.cod_unidade || null,
        cod_seg: form.cod_seg || 'GERAL', cod_cc: form.cod_cc || null,
        account_id: form.account_id || null, account_to_id: form.account_to_id || null,
        fornecedor_id: form.fornecedor_id || null, doc: form.doc || null,
        forma_pgto: form.forma_pgto || null, observacoes: form.observacoes || null,
      };
      if (entry) {
        await api.put(`/fin/ledger/entries/${entry.id}`, body);
        toast.success('Lançamento atualizado.');
      } else {
        const { data } = await api.post('/fin/ledger/entries', body);
        toast.success(data.message || 'Lançamento registrado.');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar lançamento.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-lancamento">
        <DialogHeader>
          <DialogTitle>{entry ? 'Editar lançamento' : 'Novo lançamento'}</DialogTitle>
          <DialogDescription>
            Tudo cai no mesmo razão. Transferência e ajuste movimentam só o caixa, nunca o DRE.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {KINDS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => { setKind(k); set('cod_conta', ''); }}
              data-testid={`lanc-kind-${k}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${kind === k
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-accent'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label>Data *</Label>
            <Input type="date" value={form.data || ''} onChange={(e) => set('data', e.target.value)}
              data-testid="lanc-data" />
            <p className="text-xs text-muted-foreground mt-1">Competência: {compFromDate(form.data) || '—'}</p>
          </div>
          <div>
            <Label>Valor (R$) *</Label>
            <Input type="number" step="0.01" value={form.valor || ''}
              onChange={(e) => set('valor', e.target.value)} data-testid="lanc-valor"
              placeholder={kind === 'ajuste' ? 'Use negativo para reduzir o saldo' : '0,00'} />
          </div>

          <div className="sm:col-span-2">
            <Label>Histórico *</Label>
            <Input value={form.historico || ''} onChange={(e) => set('historico', e.target.value)}
              placeholder="Ex.: Aluguel junho/2026" data-testid="lanc-historico" />
          </div>

          {tipoAlvo && (
            <div className="sm:col-span-2">
              <Label>Categoria ({tipoAlvo}) *</Label>
              <Select value={form.cod_conta || ''} onValueChange={(v) => set('cod_conta', v)}>
                <SelectTrigger data-testid="lanc-conta"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                <SelectContent>
                  {contasDoTipo.map((c) => (
                    <SelectItem key={c.cod_conta} value={c.cod_conta}>
                      {c.cod_conta} · {c.descricao}{c.classe ? ` (${c.classe})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!contasDoTipo.length && (
                <p className="text-xs text-amber-600 mt-1">
                  Nenhuma categoria {tipoAlvo} cadastrada — crie na aba Categorias.
                </p>
              )}
            </div>
          )}

          <div>
            <Label>{kind === 'transferencia' ? 'Conta de origem *' : 'Conta financeira'}
              {['receita', 'despesa'].includes(kind) && !form.due_date && ' *'}
            </Label>
            <Select value={form.account_id || ''} onValueChange={(v) => set('account_id', v)}>
              <SelectTrigger data-testid="lanc-conta-financeira"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            {['receita', 'despesa'].includes(kind) && (
              <p className="text-xs text-muted-foreground mt-1">
                {form.due_date
                  ? 'Sem conta agora: vai para Contas a Pagar e você dá baixa em 1 clique.'
                  : 'Obrigatória para o caixa nunca ter lançamento órfão.'}
              </p>
            )}
          </div>

          {['receita', 'despesa'].includes(kind) && (
            <div>
              <Label>Vencimento {kind === 'despesa' ? '(se ainda não foi pago)' : '(se ainda não recebeu)'}</Label>
              <Input type="date" value={form.due_date || ''}
                onChange={(e) => set('due_date', e.target.value)} data-testid="lanc-vencimento" />
              {!!form.due_date && (
                <p className="text-xs text-amber-700 mt-1">
                  Vira PREVISTO: entra no DRE da competência e no caixa só quando você marcar como pago.
                  Avisamos 2 dias antes e no dia.
                </p>
              )}
            </div>
          )}

          {kind === 'transferencia' && (
            <div>
              <Label>Conta de destino *</Label>
              <Select value={form.account_to_id || ''} onValueChange={(v) => set('account_to_id', v)}>
                <SelectTrigger data-testid="lanc-conta-destino"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => a.id !== form.account_id)
                    .map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {kind !== 'transferencia' && kind !== 'ajuste' && (
            <>
              <div>
                <Label>Unidade</Label>
                <Select value={form.cod_unidade || ''} onValueChange={(v) => set('cod_unidade', v)}>
                  <SelectTrigger data-testid="lanc-unidade"><SelectValue placeholder="Unidade" /></SelectTrigger>
                  <SelectContent>
                    {unidades.map((u) => (
                      <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Segmento</Label>
                <Select value={form.cod_seg || 'GERAL'} onValueChange={(v) => set('cod_seg', v)}>
                  <SelectTrigger data-testid="lanc-segmento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {segmentos.map((s) => (
                      <SelectItem key={s.cod_seg} value={s.cod_seg}>{s.nome_seg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Centro de custo</Label>
                <Select value={form.cod_cc || ''} onValueChange={(v) => set('cod_cc', v)}>
                  <SelectTrigger data-testid="lanc-cc"><SelectValue placeholder="Padrão da categoria" /></SelectTrigger>
                  <SelectContent>
                    {centrosCusto.map((c) => (
                      <SelectItem key={c.cod_cc} value={c.cod_cc}>{c.nome_cc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Documento / NF</Label>
                <Input value={form.doc || ''} onChange={(e) => set('doc', e.target.value)}
                  data-testid="lanc-doc" />
              </div>
              <div className="sm:col-span-2">
                <Label>Fornecedor / cliente</Label>
                <FornecedorPicker value={form.fornecedor_id} valueName={form.fornecedor_nome}
                  onChange={(id, nome) => setForm((f) => ({ ...f, fornecedor_id: id, fornecedor_nome: nome }))} />
              </div>
            </>
          )}

          <div className="sm:col-span-2">
            <Label>Observações</Label>
            <Textarea rows={2} value={form.observacoes || ''}
              onChange={(e) => set('observacoes', e.target.value)} data-testid="lanc-obs" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={busy} data-testid="lanc-salvar">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {entry ? 'Salvar alterações' : 'Registrar lançamento'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LancamentoModal;
